import { useEffect } from 'react';

// ── THE COLOUR IOS PAINTS OUTSIDE THE PAGE ───────────────────────────────────
// With `viewport-fit=cover` the document extends under the status bar and under
// Safari's toolbar, and iOS tints both strips from the page CANVAS — the
// background of <html>, falling back to <body>'s. `<meta name="theme-color">`
// is the secondary hint.
//
// THE BUG THIS EXISTS FOR. Landing and AuthLayout fix their own dark palette on
// a CHILD element (`.landing`, `.al-root`), so the canvas underneath them kept
// following the site theme. Measured on /register: the page painted
// rgb(11,13,18) while the canvas stayed rgb(243,244,248) under the light theme
// — a near-white bar above and below a near-black screen. Under dark it was
// rgb(18,20,28) against rgb(11,13,18): subtler, still a visible seam.
//
// Setting it on <html> rather than <body> is deliberate: when <html> has no
// background of its own the canvas takes <body>'s, but once <html> has one it
// wins, and the canvas is what covers the safe areas.

// Which page, if any, has claimed the chrome. Without this the theme provider's
// own effect would overwrite a page's fixed colour the moment the theme
// changed — the landing and the auth screens ignore the site theme by design,
// and their chrome has to ignore it too.
let claimed = 0;

/**
 * Paints the browser chrome. Exported so ThemeProvider can keep the default in
 * step with the theme; page-level callers should use the hook below.
 *
 * @param {string}  color
 * @param {boolean} [force]  bypass the claim — only the claiming page should.
 */
export function setBrowserChrome(color, force = false) {
  if (typeof document === 'undefined' || !color) return;
  if (claimed > 0 && !force) return;

  let meta = document.head.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', color);
  document.documentElement.style.backgroundColor = color;
}

/**
 * Claims the browser chrome for as long as the component is mounted, then puts
 * back whatever was there before.
 *
 * For pages that paint their own palette regardless of the site theme. A page
 * that simply uses the `--site-*` tokens needs nothing — ThemeProvider already
 * keeps the chrome matching those.
 *
 * @param {string} color  e.g. '#0B0D12'
 */
export default function usePageChrome(color) {
  useEffect(() => {
    if (!color) return undefined;

    const prevMeta = document.head
      .querySelector('meta[name="theme-color"]')?.getAttribute('content') || '';
    const prevHtml = document.documentElement.style.backgroundColor;

    claimed += 1;
    setBrowserChrome(color, true);

    return () => {
      claimed = Math.max(0, claimed - 1);
      // Restore rather than recompute: the previous value may have come from
      // the theme OR from another page that is still mounted above this one.
      const meta = document.head.querySelector('meta[name="theme-color"]');
      if (meta && prevMeta) meta.setAttribute('content', prevMeta);
      document.documentElement.style.backgroundColor = prevHtml;
    };
  }, [color]);
}
